import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Backend from "../../../../Backend/Backend";

const backend = await new Backend().init();

export default function EditCommission({
  open,
  onClose,
  user,
  admin,
  type,
  fetchUsers,
}) {
  const userKey = type?.key;
  const getUserVal = (key) => user?.[`${key}_${userKey}`] ?? "";
  const getUserMarginCarry = (key) => user?.[`${key}_${userKey}_carry`] ?? "";
  const getMcxLot = (key) => user?.[`${key}_${userKey}_lot`] ?? "";
  const getMcxVolume = (key) => user?.[`${key}_${userKey}_volume`] ?? "";

  const [newMultiple, setNewMultiple] = useState(getUserVal("symbol_multiple"));
  const [newBrokerage, setNewBrokerage] = useState(getUserVal("b"));
  const [newBrokerageLot, setNewBrokerageLot] = useState(
    getMcxLot("b_mcx_lot"),
  );
  const [newBrokerageVolume, setNewBrokerageVolume] = useState(
    getMcxVolume("b"),
  );
  const [newBrokerBrokerage, setNewBrokerBrokerage] = useState(getUserVal("b"));
  const [newSubBrokerBrokerage, setNewSubBrokerBrokerage] = useState(
    getUserVal("sb"),
  );
  const [newMargin, setNewMargin] = useState(getUserVal("margin"));
  const [newMarginCarry, setNewMarginCarry] = useState(
    getUserMarginCarry("margin"),
  );
  const [isLoading, setIsLoading] = useState(false);

  const adminValues = useMemo(
    () => ({
      multiple: admin?.[`symbol_multiple_${userKey}`] ?? 0,
      brokerage: admin?.[`b_${userKey}`] ?? 0,
      brokerageLot: admin?.[`b_${userKey}_lot`] ?? 0,
      brokerageVolume: admin?.[`b_${userKey}_volume`] ?? 0,
      margin: admin?.[`margin_${userKey}`] ?? 0,
      marginCarry: admin?.[`margin_${userKey}_carry`] ?? 0,
    }),
    [admin, userKey],
  );

  useEffect(() => {
    setNewMultiple(getUserVal("symbol_multiple"));
    setNewBrokerage(getUserVal("b"));
    setNewBrokerageLot(getMcxLot("b"));
    setNewBrokerageVolume(getMcxVolume("b"));
    setNewBrokerBrokerage(getUserVal("b"));
    setNewSubBrokerBrokerage(getUserVal("sb"));
    setNewMargin(getUserVal("margin"));
    setNewMarginCarry(getUserMarginCarry("margin"));
  }, [userKey, user]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    const num = (val, fallback) =>
      val !== "" ? Number(val) : Number(fallback ?? 0);

    const payload = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),
      user_id: user.id,
      type: userKey,
      user: "0",

      user_multiple: num(newMultiple, getUserVal("symbol_multiple")),
      user_broker_brokerage: num(newBrokerBrokerage, getUserVal("bb")),
      user_sub_broker_brokerage: num(newSubBrokerBrokerage, getUserVal("sb")),
      user_margin: num(newMargin, getUserVal("margin")),
      user_margin_carry: num(newMarginCarry, getUserMarginCarry("margin")),
    };

    if (userKey === "mcx") {      
      payload.user_brokerage_lot = num(newBrokerageLot, getMcxLot("b"));
      payload.user_brokerage_volume = num(
        newBrokerageVolume,
        getMcxVolume("b"),
      );
    } else {
      payload.user_brokerage = num(newBrokerage, getUserVal("b"));
    }

    try {
      const response = await backend.edit_user_commission_new(payload);
      if (response.error === "False") {
        toast.success(response.message);
        fetchUsers();
        onClose();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Network Error, Failed to update commission.");
    } finally {
      setIsLoading(false);
    }
  }, [
    newMultiple,
    newBrokerage,
    newBrokerageLot,
    newBrokerageVolume,
    newBrokerBrokerage,
    newSubBrokerBrokerage,
    newMargin,
    newMarginCarry,
    userKey,
    user,
    fetchUsers,
    onClose,
  ]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[99999]">
      <div className="bg-[var(--background)] border-2 border-[var(--hover)] rounded-xl shadow-lg w-[90%] max-w-md p-6">
        <h2 className="text-lg font-bold text-[var(--text)]">
          ✏️ Edit Commission - {type?.title}
        </h2>

        <div className="space-y-4 mt-4">
          <InputField
            label="Multiple"
            value={newMultiple}
            setValue={setNewMultiple}
            min={adminValues.multiple}
          />

          {userKey === "mcx" ? (
            <>
              <InputField
                label="Brokerage Lot Wise"
                value={newBrokerageLot}
                setValue={setNewBrokerageLot}
                min={adminValues.brokerageLot}
              />
              <InputField
                label="Brokerage Volume"
                value={newBrokerageVolume}
                setValue={setNewBrokerageVolume}
                min={adminValues.brokerageVolume}
              />
            </>
          ) : (
            <InputField
              label="Brokerage"
              value={newBrokerage}
              setValue={setNewBrokerage}
              min={adminValues.brokerage}
            />
          )}

          {user.broker_id !== 0 && (
            <InputField
              label="Broker Brokerage"
              value={newBrokerBrokerage}
              setValue={setNewBrokerBrokerage}
              min={adminValues.brokerage}
            />
          )}

          {user.sub_broker_id !== 0 && (
            <InputField
              label="Sub Broker Brokerage"
              value={newSubBrokerBrokerage}
              setValue={setNewSubBrokerBrokerage}
              min={adminValues.brokerage}
            />
          )}

          <InputField
            label="Margin"
            value={newMargin}
            setValue={setNewMargin}
            min={adminValues.margin}
          />
          {user.intraday_sq === 1 ? (
            <InputField
              label="Margin Carry"
              value={newMarginCarry}
              setValue={setNewMarginCarry}
              min={adminValues.marginCarry}
            />
          ) : null}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[var(--sell)] text-[var(--white)] rounded-lg text-sm font-semibold transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[var(--primary)] rounded-lg text-sm font-semibold text-white transition"
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, value, setValue, min }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[var(--text)]">
        {label}
      </label>

      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="mt-1 w-full border-1 border-[var(--hover)] bg-[var(--background)] text-[var(--text)]
        rounded-lg px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-[var(--primary)]"
        placeholder={`Enter ${label}`}
      />

      <p className="text-xs text-[var(--subtext)] mt-1">Admin Min: {min}</p>
    </div>
  );
}
